import React, { useState, useEffect, useRef } from "react";
import PreviaPedidos from "./PreviaPedidos";
import Nav from "./Nav";
import Paginador from "./Paginador";
import PedidoForm from "./PedidoForm";
import {
  collection,
  deleteDoc,
  getFirestore,
  doc,
  onSnapshot,
  updateDoc,
  getDocs,
} from "firebase/firestore";
import { db } from "../firebaseConfig";
import { useNavContext } from "../context/NavContext";
import Swal from "sweetalert2";
import { useLocation, useNavigate } from "react-router-dom";
import Modal from "react-modal";
import moment from "moment";
import Spinner from "./Spinner";
import BalanceModal from "./BalanceModal";
import { useFunctionsPrevia } from "../hooks/useFunctionsPrevia";

moment.locale("es");

const styleForm = {
  content: {
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    paddingLeft: 0,
    padding: 0,
    fontFamily: "Roboto, sans-serif",
    "::WebkitScrollbar": {
      width: "0px",
      background: "transparent",
    },
    msOverflowStyle: "none",
    scrollbarWidth: "none",
  },
};

export const customStyles = {
  content: {
    top: "0",
    left: "0",
    right: "0",
    bottom: "0",
    padding: "0",
    border: "none",
    borderRadius: "0", // Si quieres sin bordes redondeados para pantalla completa
    boxShadow: "none",
    width: "100vw",
    height: "100vh",
    overflow: "auto",
    fontFamily: "Roboto, sans-serif",
    /* Opciones para ocultar scrollbars */
    "::WebkitScrollbar": {
      width: "0px",
      background: "transparent",
    },
    msOverflowStyle: "none",
    scrollbarWidth: "none",
  },
  overlay: {
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    zIndex: 102,
  },
};

const pop = {
  containerBase: {
    background: "#ffffff",
    border: "1px solid #e5e7eb",
    borderRadius: 4,
    padding: "10px 8px 8px",
    width: 170, // ← ancho fijo y angosto
    minWidth: 0, // ← quita la barrera de minWidth
    fontSize: 12, // ← más compacto
    boxShadow: "0 16px 32px rgba(0,0,0,0.18)",
    boxSizing: "border-box",
  },
  divider: { height: 1, background: "#eef0f3", margin: "8px 0 6px" },
  optionBase: {
    width: "100%",
    borderRadius: 3,
    padding: "6px 8px", // ← menos padding lateral
    fontSize: 12,
    fontWeight: 600,
    textAlign: "center",
    cursor: "pointer",
    marginBottom: 6,
    border: "1px solid transparent",
    transition: "filter .15s ease-in-out, transform .02s ease-in-out",
    boxSizing: "border-box",
    whiteSpace: "nowrap", // ← no saltos de línea
    overflow: "hidden",
    textOverflow: "ellipsis", // ← puntos suspensivos si no cabe
  },
  stylesByEstado: {
    Pendiente: {
      background: "rgba(211,211,211,.3)",
      color: "#A9A9A9",
      border: "1px solid #A9A9A9",
    },
    "En Proceso": {
      background: "rgba(255,255,0,.2)",
      color: "#FFB800",
      border: "1px solid #FFD966",
    },
    Facturado: {
      background: "#f3e8ff",
      color: "#a855f7",
      border: "1px solid #a855f7",
    },
    Pagada: {
      background: "rgba(144,238,144,.2)",
      color: "#4CAF50",
      border: "1px solid #4CAF50",
    },
  },
};

const truncate = (text, max = 40) => {
  const t = (text ?? "").toString().trim();
  if (t.length <= max) return t || "—";
  const cut = t.slice(0, max);
  const lastSpace = cut.lastIndexOf(" ");
  const safe = lastSpace > 20 ? cut.slice(0, lastSpace) : cut; // si hay espacio cerca, corta ahí
  return safe.trimEnd() + "…";
};

// aplica el estilo según estado
const optionStyle = (label) => ({
  ...pop.optionBase,
  ...(pop.stylesByEstado[label] || pop.stylesByEstado["Pendiente"]),
});

// Debounce simple
function useDebouncedValue(value, delay = 250) {
  const [v, setV] = React.useState(value);
  React.useEffect(() => {
    const t = setTimeout(() => setV(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return v;
}

function TablaPedidos({
  ordenes,
  clientes,
  setOrdenes,
  guardarOrden,
  modoEdicion,
  orden,
}) {
  // Estados para manejar el modal y la orden seleccionada
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [ordenSeleccionada, setOrdenSeleccionada] = useState(null);
  const [loadingOrdenes, setLoadingOrdenes] = useState(true);
  const [selectedOrdenes, setSelectedOrdenes] = useState([]);
  const [ordenamiento, setOrdenamiento] = useState({
    campo: "fechaOrden",
    ascendente: false,
  });
  const [mostrarFormulario, setMostrarFormulario] = useState(false);
  const [showNuevoButton, setShowNuevoButton] = useState(true);
  const { searchTerm, setSearchTerm, proximasAVencer, proximosEventos } =
    useNavContext();
  const debouncedTerm = useDebouncedValue(searchTerm, 300); // 300ms suele sentirse bien
  const location = useLocation();
  const navigate = useNavigate();
  const [estadosCeldas, setEstadosCeldas] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);
  const [formDirty, setFormDirty] = useState(false);
  const [cotizacionParaOrden, setCotizacionParaOrden] = useState(null);
  const [filterDivision, setFilterDivision] = useState("Todos");
  const [ordenesConGastos, setOrdenesConGastos] = useState([]);
  const [showBalanceModal, setShowBalanceModal] = useState(false);
  const [balanceOrderIds, setBalanceOrderIds] = useState([]);
  const ESTADOS = ["Pendiente", "En Proceso", "Facturado", "Pagada"];
  const { formatDate } = useFunctionsPrevia();

  // Popover para elegir estado
  const [statusMenu, setStatusMenu] = useState({
    open: false,
    orderId: null,
    rect: null, // guardamos el rect del chip
  });

  const popoverRef = useRef(null);
  const [coords, setCoords] = useState({
    leftCenter: 0,
    top: 0,
    caretX: 0,
    place: "bottom", // 'bottom' o 'top'
  });

  const handleEditar = (orden) => {
    // Cierra la vista previa
    setModalIsOpen(false);
    // Selecciona la orden que vamos a editar
    setOrdenSeleccionada(orden);
    // Abre el formulario
    setMostrarFormulario(true);
    setShowNuevoButton(false);
  };

  // Limpia la búsqueda global cuando entras a Órdenes de compra
  useEffect(() => {
    setSearchTerm(""); // al montar, deja la búsqueda vacía

    return () => {
      setSearchTerm(""); // opcional: al desmontar, también la limpias
    };
  }, [setSearchTerm]);

  useEffect(() => {
    if (location.state && location.state.openModal) {
      setMostrarFormulario(true);
    }
  }, [location]);

  useEffect(() => {
    const convertir = location.state?.convertirEnOrden;
    const cot = location.state?.cotizacion;

    if (convertir && cot) {
      setCotizacionParaOrden(cot);
      setMostrarFormulario(true);
      navigate("/orden-de-compra", { replace: true });
    }
  }, [location, navigate]);

  const handleItemsPerPageChange = (items) => {
    setItemsPerPage(items);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleFormDirtyChange = (dirty) => {
    setFormDirty(dirty);
  };

  useEffect(() => {
    // Si no hay órdenes cargadas, limpia “ordenesConGastos”
    if (!ordenes || ordenes.length === 0) {
      setOrdenesConGastos([]);
      return;
    }

    // Función auxiliar para traer las descripciones de gastos de una orden
    const cargarGastosDe = async (ordenId) => {
      const todasDesc = [];

      // 1) Obtiene todas las carpetas (listasGastos) de esa orden
      const carpetasSnap = await getDocs(
        collection(db, `pedidos/${ordenId}/listasGastos`)
      );

      // 2) Para cada carpeta, lee sus “productosGastos” y extrae el campo “descripcion”
      for (const carpetaDoc of carpetasSnap.docs) {
        const folderId = carpetaDoc.id;
        const gastosSnap = await getDocs(
          collection(
            db,
            `pedidos/${ordenId}/listasGastos/${folderId}/productosGastos`
          )
        );

        gastosSnap.docs.forEach((gastoDoc) => {
          const d = gastoDoc.data().descripcion;
          if (d && typeof d === "string") {
            todasDesc.push(d.toLowerCase().trim());
          }
        });
      }

      return todasDesc; // array de strings (minúscula)
    };

    // 3) Para cada orden, dispara la promesa que trae sus descripciones
    (async () => {
      const promesas = ordenes.map(async (orden) => {
        const descGastos = await cargarGastosDe(orden.id);
        return {
          ...orden,
          gastosDescriptions: descGastos, // array de descripciones en minúscula
        };
      });

      const conGastos = await Promise.all(promesas);
      setOrdenesConGastos(conGastos);
    })();
  }, [ordenes]);

  // Abre el popover pegado al chip de estado
  const abrirMenuEstado = (e, orderId) => {
    e.stopPropagation(); // evita abrir la previa
    const r = e.currentTarget.getBoundingClientRect(); // coords de viewport
    setStatusMenu({
      open: true,
      orderId,
      rect: {
        top: r.top,
        left: r.left,
        right: r.right,
        bottom: r.bottom,
        width: r.width,
        height: r.height,
      },
    });
  };

  const cerrarMenuEstado = () =>
    setStatusMenu({ open: false, orderId: null, rect: null });

  // Actualiza en UI + Firestore cuando el usuario elige un estado
  const actualizarEstado = async (orderId, newEstado) => {
    setEstadosCeldas((prev) => ({ ...prev, [orderId]: newEstado }));
    const firestore = getFirestore();
    const ref = doc(firestore, "pedidos", orderId);
    await updateDoc(ref, { estado: newEstado });
    cerrarMenuEstado();
  };

  const openFormulario = () => {
    setOrdenSeleccionada(null);
    setCotizacionParaOrden(null);
    setMostrarFormulario(true);
    setShowNuevoButton(false);
  };

  const closeModalFormulario = () => {
    if (formDirty) {
      Swal.fire({
        title: "¿Seguro que quieres salir?",
        text: "Se perderán los cambios realizados",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: "Sí, salir",
        cancelButtonText: "Cancelar",
      }).then((result) => {
        if (result.isConfirmed) {
          setMostrarFormulario(false);
          setShowNuevoButton(true);
          setFormDirty(false);
        }
      });
    } else {
      setMostrarFormulario(false);
      setShowNuevoButton(true);
    }
  };

  useEffect(() => {
    const firestore = getFirestore();
    const ordenesRef = collection(firestore, "pedidos");

    const unsubscribe = onSnapshot(ordenesRef, (snapshot) => {
      const updatedOrdenes = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      setOrdenes(updatedOrdenes);

      const initialEstados = {};
      updatedOrdenes.forEach((orden) => {
        initialEstados[orden.id] = orden.estado || "Pendiente";
      });
      setEstadosCeldas(initialEstados);

      setLoadingOrdenes(false);
    });

    return () => unsubscribe();
  }, [setOrdenes]);

  useEffect(() => {
    if (!statusMenu.open || !statusMenu.rect) return;

    const margin = 12;

    const compute = () => {
      const el = popoverRef.current;
      const popW = el ? el.offsetWidth : 240; // fallback
      const popH = el ? el.offsetHeight : 200;

      const { innerWidth: vw, innerHeight: vh } = window;
      const r = statusMenu.rect;

      // Centro horizontal del chip
      let leftCenter = r.left + r.width / 2;

      // Clampeamos para que el popover completo no se salga
      const minCenter = margin + popW / 2;
      const maxCenter = vw - margin - popW / 2;
      leftCenter = Math.max(minCenter, Math.min(maxCenter, leftCenter));

      // Preferimos abrir abajo; si no cabe, arriba
      let top = r.bottom + margin;
      let place = "bottom";
      if (top + popH + margin > vh) {
        top = r.top - popH - margin;
        place = "top";
        if (top < margin) top = margin; // última barrera
      }

      // Posición de la flechita dentro del popover
      let caretX = r.left + r.width / 2 - (leftCenter - popW / 2);
      caretX = Math.max(16, Math.min(popW - 16, caretX)); // no pegar a bordes

      setCoords({ leftCenter, top, caretX, place });
    };

    compute();
    const onReflow = () => compute();
    window.addEventListener("resize", onReflow);
    window.addEventListener("scroll", onReflow, true);

    return () => {
      window.removeEventListener("resize", onReflow);
      window.removeEventListener("scroll", onReflow, true);
    };
  }, [statusMenu.open, statusMenu.rect]);

  const handleDeleteSelected = async () => {
    try {
      const confirmed = await Swal.fire({
        title: "¿Estás seguro?",
        text: "Esta acción eliminará las órdenes seleccionadas.",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: "Sí, eliminar",
        cancelButtonText: "Cancelar",
        scrollbarPadding: false,
      });

      if (confirmed.isConfirmed) {
        const firestore = getFirestore();
        const ordenesEliminadas = [];
        const ordenesRef = collection(firestore, "pedidos");

        for (const ordenId of selectedOrdenes) {
          const ordenDocRef = doc(ordenesRef, ordenId);
          await deleteDoc(ordenDocRef);
          ordenesEliminadas.push(ordenId);
        }

        const ordenesRestantes = ordenes.filter(
          (orden) => !ordenesEliminadas.includes(orden.id)
        );
        setOrdenes(ordenesRestantes);
        setSelectedOrdenes([]);

        await Swal.fire({
          title: "¡Orden eliminada!",
          text: "Las órdenes seleccionadas han sido eliminadas correctamente.",
          icon: "success",
          confirmButtonColor: "#3085d6",
          confirmButtonText: "Ok",
          scrollbarPadding: false,
        });
      }
    } catch (error) {
      console.error("Error al eliminar órdenes:", error);
    }
  };

  // Al hacer clic en la fila, abrimos el modal con los detalles
  const handleRowClick = (orden) => {
    setOrdenSeleccionada(orden);
    setModalIsOpen(true);
  };

  const cerrarModalPrevia = () => {
    setModalIsOpen(false);
    setOrdenSeleccionada(null);
  };

  const handleSelectOrden = (ordenId) => {
    setSelectedOrdenes((prevSelected) => {
      if (prevSelected.includes(ordenId)) {
        return prevSelected.filter((id) => id !== ordenId);
      } else {
        return [...prevSelected, ordenId];
      }
    });
  };

  const handleOrdenamientoChange = (campo) => {
    setOrdenamiento((prevOrdenamiento) => ({
      campo,
      ascendente:
        campo === prevOrdenamiento.campo ? !prevOrdenamiento.ascendente : true,
    }));
  };

  const ordenarOrdenes = (ordenes, { campo, ascendente }) => {
    if (!ordenes || ordenes.length === 0) return [];
    const sortedOrdenes = [...ordenes];
    const dir = ascendente ? 1 : -1;

    switch (campo) {
      case "fechaOrden":
        sortedOrdenes.sort(
          (a, b) =>
            dir * (new Date(a.fechaOrden || 0) - new Date(b.fechaOrden || 0))
        );
        break;

      case "numeroOrdenDeCompra":
        sortedOrdenes.sort(
          (a, b) =>
            dir * ((a.numeroOrdenDeCompra || 0) - (b.numeroOrdenDeCompra || 0))
        );
        break;

      case "nombreCliente":
        sortedOrdenes.sort(
          (a, b) =>
            dir *
            (a.nombreCliente || "").localeCompare(b.nombreCliente || "", "es", {
              sensitivity: "base",
            })
        );
        break;

      // NUEVO: ordenar por Descripción (referencia)
      case "referencia":
        sortedOrdenes.sort(
          (a, b) =>
            dir *
            (a.referencia || "").localeCompare(b.referencia || "", "es", {
              sensitivity: "base",
            })
        );
        break;

      case "nombreProveedor":
        sortedOrdenes.sort(
          (a, b) =>
            dir *
            (a.nombreProveedor || "").localeCompare(
              b.nombreProveedor || "",
              "es",
              {
                sensitivity: "base",
              }
            )
        );
        break;

      case "estado":
        sortedOrdenes.sort(
          (a, b) =>
            dir *
            (a.estado || "").localeCompare(b.estado || "", "es", {
              sensitivity: "base",
            })
        );
        break;

      case "total":
        sortedOrdenes.sort(
          (a, b) =>
            dir * ((parseFloat(a.total) || 0) - (parseFloat(b.total) || 0))
        );
        break;

      default:
        break;
    }

    return sortedOrdenes;
  };

  const handleSelectAll = (event) => {
    if (event.target.checked) {
      const allOrdenesIds = filteredOrdenes.map((orden) => orden.id);
      setSelectedOrdenes(allOrdenesIds);
    } else {
      setSelectedOrdenes([]);
    }
  };

  const filteredOrdenes = React.useMemo(() => {
    const termino = debouncedTerm.trim().toLowerCase();
    const searchWords = termino.split(" ").filter(Boolean);

    const generarFechas = (fecha) => {
      if (!fecha) return [];
      const dateObj = new Date(fecha);

      const form1 = dateObj
        .toLocaleDateString("es-ES", {
          day: "2-digit",
          month: "short",
          year: "numeric",
        })
        .toLowerCase();

      const day = String(dateObj.getDate()).padStart(2, "0");
      const mesesAbrev = [
        "ene",
        "feb",
        "mar",
        "abr",
        "may",
        "jun",
        "jul",
        "ago",
        "sep",
        "oct",
        "nov",
        "dic",
      ];
      const mesAbrev = mesesAbrev[dateObj.getMonth()];
      const year2 = String(dateObj.getFullYear()).slice(-2);
      const form2 = `${day} ${mesAbrev} ${year2}`.toLowerCase();

      const form3 = dateObj
        .toLocaleDateString("es-ES", {
          day: "2-digit",
          month: "long",
          year: "numeric",
        })
        .toLowerCase();

      return [form1, form2, form3];
    };

    // Función genérica de filtrado. Si includeGastos=true usa gastosDescriptions.
    const filtrar = (lista, includeGastos) => {
      if (!lista || lista.length === 0) return [];

      let data = lista;
      if (filterDivision !== "Todos") {
        data = data.filter((o) => o.division === filterDivision);
      }

      if (termino === "") return data; // sin texto → muestra todo

      return data.filter((orden) => {
        const totalNum = parseFloat(orden.total) || 0;
        const formattedImporte = `${orden.divisa || ""} ${totalNum
          .toFixed(2)
          .replace(/\B(?=(\d{3})+(?!\d))/g, ",")}`.toLowerCase();

        const base = [
          orden.numeroOrdenDeCompra?.toString().toLowerCase() || "",
          orden.referencia?.toLowerCase() || "",
          orden.nombreProveedor?.toLowerCase() || "",
          orden.nombreCliente?.toLowerCase() || "",
          orden.estado?.toLowerCase() || "",
          orden.vendedorNombre?.toLowerCase() || "",
          formattedImporte,
          orden.divisa?.toLowerCase() || "",
          orden.planta?.toLowerCase() || "",
          orden.area?.toLowerCase() || "",
          orden.asunto?.toLowerCase() || "",
          ...generarFechas(orden.fechaOrden),
          ...generarFechas(orden.fechaFinRango),
          ...generarFechas(orden.fechaCotizacion),
        ];

        const gastos = includeGastos ? orden.gastosDescriptions || [] : [];

        const searchableFields = [...base, ...gastos];

        return searchableFields.some((field) =>
          searchWords.every((word) => field.includes(word))
        );
      });
    };

    // Fallback: si aún no tenemos ordenesConGastos, filtra sobre "ordenes".
    if (!ordenesConGastos || ordenesConGastos.length === 0) {
      return filtrar(ordenes, false);
    }

    // Ya está listo el índice con gastos → úsalo
    return filtrar(ordenesConGastos, true);
  }, [ordenesConGastos, ordenes, debouncedTerm, filterDivision]);

  const paginatedOrdenes = React.useMemo(() => {
    const ordenesOrdenadas = ordenarOrdenes(filteredOrdenes, ordenamiento);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return ordenesOrdenadas.slice(startIndex, endIndex);
  }, [filteredOrdenes, currentPage, itemsPerPage, ordenamiento]);

  const totalOrdenes = filteredOrdenes.length;

  // Oculta scrollbars para cualquier elemento con data-popover-scroll
  const HIDE_SCROLLBARS = `
  [data-popover-scroll]{
    -ms-overflow-style: none;     /* IE/Edge */
    scrollbar-width: none;        /* Firefox */
    overscroll-behavior: contain; /* evita arrastrar el scroll del body */
  }
  [data-popover-scroll]::-webkit-scrollbar{
    width: 0;
    height: 0;
  }
  `;

  return (
    <>
      <style>{HIDE_SCROLLBARS}</style>
      {/* Barra de búsqueda y botones */}
      {!mostrarFormulario && (
        <>
          <Nav
            handleSearch={setSearchTerm}
            proximasAVencer={proximasAVencer}
            proximosEventos={proximosEventos}
          />

          <div className="task-filters">
            <div className="header-actions-proyectos">
              <div className="buscador-pedidos">
                <i className="bi bi-search" style={{ paddingLeft: "10px" }}></i>
                <input
                  type="text"
                  className="buscador-input"
                  placeholder="Buscar órdenes..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>
            <div className="task-filters-right">
              <div className="view-buttons-container">
                <button
                  type="button"
                  className="delete-button"
                  style={{
                    visibility:
                      selectedOrdenes.length > 0 ? "visible" : "hidden",
                    opacity: selectedOrdenes.length > 0 ? 1 : 0,
                    transform:
                      selectedOrdenes.length > 0 ? "scale(1)" : "scale(0.9)",
                    transition:
                      "visibility 0s linear 0.2s, opacity 0.2s ease-in-out, transform 0.2s ease-in-out",
                  }}
                  onClick={handleDeleteSelected}
                >
                  Eliminar
                </button>

                <div
                  className="delete-button-container"
                  style={{ display: "inline-block", marginLeft: 8 }}
                >
                  <button
                    type="button"
                    className="delete-button balance-button"
                    style={{
                      backgroundColor: "#007bff",
                      visibility:
                        selectedOrdenes.length > 0 ? "visible" : "hidden",
                      opacity: selectedOrdenes.length > 0 ? 1 : 0,
                      transition:
                        "visibility 0s linear 0.2s, opacity 0.2s ease-in-out, transform 0.2s ease-in-out",
                    }}
                    onClick={() => {
                      console.log(
                        "🔍 IDs seleccionados para balance:",
                        selectedOrdenes
                      );
                      setBalanceOrderIds(selectedOrdenes);
                      setShowBalanceModal(true);
                    }}
                  >
                    Balance
                  </button>
                </div>

                <select
                  value={filterDivision}
                  onChange={(e) => setFilterDivision(e.target.value)}
                  className="division-select"
                >
                  <option value="Todos">Todos</option>
                  <option value="Magno Hernández">Magno Hernández</option>
                  <option value="Cesar Zuñiga">Cesar Zuñiga</option>
                </select>

                <button className="view-button">
                  <i className="bi bi-three-dots-vertical"></i>
                </button>

                <button className="exportar-button">
                  <i className="bi bi-cloud-arrow-down"></i>
                  <span>Exportar</span>
                </button>

                {showNuevoButton && (
                  <button className="new-task-button" onClick={openFormulario}>
                    + Crear Nuevo
                  </button>
                )}
              </div>
            </div>
          </div>
        </>
      )}

      <div className="cotizaciones-table">
        {/* Tabla principal de pedidos (sin BandejaPedidos/ResumenPedidos) */}
        <div className="tabla-cotizaciones-wrapper">
          <div className="tabla-cotizaciones-content">
            {loadingOrdenes ? (
              <Spinner />
            ) : (
              <>
                {filteredOrdenes.length > 0 ? (
                  <>
                    <table>
                      {!mostrarFormulario && (
                        <thead>
                          <tr>
                            <th>
                              <input
                                type="checkbox"
                                onChange={handleSelectAll}
                                className="checkbox-select-all check"
                                checked={
                                  selectedOrdenes.length ===
                                  filteredOrdenes.length
                                }
                              />
                            </th>

                            <th
                              onClick={() =>
                                handleOrdenamientoChange("fechaOrden")
                              }
                            >
                              Fecha
                            </th>

                            <th
                              onClick={() =>
                                handleOrdenamientoChange("numeroOrdenDeCompra")
                              }
                            >
                              No. Orden
                            </th>

                            <th
                              onClick={() =>
                                handleOrdenamientoChange("nombreCliente")
                              }
                            >
                              Cliente
                            </th>

                            {/* NUEVO: Descripción entre Cliente y Estado */}
                            <th
                              onClick={() =>
                                handleOrdenamientoChange("referencia")
                              }
                            >
                              Descripción
                            </th>

                            <th
                              onClick={() => handleOrdenamientoChange("estado")}
                            >
                              Estado
                            </th>

                            <th
                              onClick={() => handleOrdenamientoChange("total")}
                            >
                              Importe
                            </th>
                          </tr>
                        </thead>
                      )}
                      <tbody>
                        {paginatedOrdenes.map((orden, index) => (
                          <tr key={index}>
                            <td className="check">
                              <input
                                type="checkbox"
                                checked={selectedOrdenes.includes(orden.id)}
                                onChange={() => handleSelectOrden(orden.id)}
                                className="checkbox"
                              />
                            </td>

                            <td onClick={() => handleRowClick(orden)}>
                              <div className="estilos-fechas">
                                {formatDate(orden.fechaOrden)}
                              </div>
                            </td>

                            <td
                              style={{ color: "#007bff", fontWeight: "500" }}
                              onClick={() => handleRowClick(orden)}
                            >
                              {orden.numeroOrdenDeCompra
                                ?.toString()
                                .padStart(4, "0")}
                            </td>

                            {/* Cliente */}
                            <td onClick={() => handleRowClick(orden)}>
                              {orden.nombreCliente}
                            </td>

                            {/* Descripción */}
                            <td onClick={() => handleRowClick(orden)}>
                              <span title={orden.referencia || "—"}>
                                {truncate(orden.referencia, 40)}
                              </span>
                            </td>

                            {/* CHIP DE ESTADO -> ABRE MENU */}
                            <td>
                              <div
                                className={`status ${
                                  estadosCeldas[orden.id] === "Pendiente"
                                    ? "pendiente"
                                    : estadosCeldas[orden.id] === "En Proceso"
                                    ? "en-proceso"
                                    : estadosCeldas[orden.id] === "Facturado"
                                    ? "facturado"
                                    : estadosCeldas[orden.id] === "Pagada"
                                    ? "pagada"
                                    : "pendiente"
                                }`}
                                onClick={(e) => abrirMenuEstado(e, orden.id)}
                              >
                                {estadosCeldas[orden.id] || "Pendiente"}
                              </div>
                            </td>

                            <td
                              className="importe"
                              onClick={() => handleRowClick(orden)}
                            >
                              {orden.divisa}{" "}
                              {parseFloat(orden.total)
                                ?.toFixed(2)
                                .replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>

                    {/* === Popover de estados (inline styles) === */}
                    {statusMenu.open && (
                      <>
                        {/* Backdrop */}
                        <div
                          onClick={cerrarMenuEstado}
                          style={{
                            position: "fixed",
                            inset: 0,
                            background: "transparent",
                            zIndex: 9998,
                          }}
                        />

                        {/* Wrapper SIN scroll (caret aquí) */}
                        <div
                          ref={popoverRef}
                          style={{
                            position: "fixed",
                            top: coords.top,
                            left: coords.leftCenter,
                            transform: "translateX(-50%)",
                            zIndex: 9999,
                            overflow: "visible", // importante: no recortar el caret
                            pointerEvents: "auto",
                          }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          {/* Caret completamente FUERA del contenedor scrollable */}
                          <span
                            style={{
                              position: "absolute",
                              [coords.place === "bottom" ? "top" : "bottom"]:
                                -8,
                              left: coords.caretX,
                              width: 16,
                              height: 16,
                              background: "#ffffff",
                              transform: "translateX(-50%) rotate(45deg)",
                              boxShadow:
                                coords.place === "bottom"
                                  ? "-3px -3px 6px rgba(0,0,0,0.03)"
                                  : "3px 3px 6px rgba(0,0,0,0.03)",
                              ...(coords.place === "bottom"
                                ? {
                                    borderLeft: "1px solid #e5e7eb",
                                    borderTop: "1px solid #e5e7eb",
                                  }
                                : {
                                    borderRight: "1px solid #e5e7eb",
                                    borderBottom: "1px solid #e5e7eb",
                                  }),
                            }}
                          />

                          {/* Contenedor REAL con scroll y estilos */}
                          <div
                            data-popover-scroll
                            style={{
                              ...pop.containerBase,
                              maxHeight: "calc(100vh - 32px)",
                              overflowY: "auto", // scroll sólo aquí
                              overflowX: "hidden",
                              scrollbarWidth: "none",
                              msOverflowStyle: "none",
                            }}
                          >
                            {ESTADOS.map((label, idx) => (
                              <button
                                key={label}
                                onClick={() =>
                                  actualizarEstado(statusMenu.orderId, label)
                                }
                                style={{
                                  ...optionStyle(label),
                                  marginTop: idx === 0 ? 2 : 0,
                                  marginBottom:
                                    idx === ESTADOS.length - 1 ? 0 : 8,
                                }}
                                onMouseEnter={(e) =>
                                  (e.currentTarget.style.filter =
                                    "brightness(1.05)")
                                }
                                onMouseLeave={(e) =>
                                  (e.currentTarget.style.filter = "none")
                                }
                                onMouseDown={(e) =>
                                  (e.currentTarget.style.transform =
                                    "scale(0.995)")
                                }
                                onMouseUp={(e) =>
                                  (e.currentTarget.style.transform = "scale(1)")
                                }
                              >
                                {label}
                              </button>
                            ))}
                          </div>
                        </div>
                      </>
                    )}
                  </>
                ) : // ⬇️ Mostrar "sin resultados" SOLO cuando el texto ya está aplicado (debounced) y no está vacío
                searchTerm === debouncedTerm && debouncedTerm.trim() !== "" ? (
                  <div className="no-resultados">
                    <div
                      className="mensaje-sin-resultados"
                      style={{ marginTop: "30px" }}
                    >
                      <p style={{ fontSize: "14px" }}>
                        No existe ninguna orden de compra que coincida con esta
                        búsqueda.
                      </p>
                    </div>
                    <div className="imagen-sin-resultados">
                      <img
                        src="/img/noSearch.svg"
                        alt="Sin resultados"
                        className="sin-resultados-imagen"
                      />
                    </div>
                  </div>
                ) : null}
              </>
            )}
            {/* Modal del formulario de creación/edición */}
            <Modal
              isOpen={mostrarFormulario}
              onRequestClose={closeModalFormulario}
              contentLabel="Orden de Compra"
              style={styleForm}
            >
              <button onClick={closeModalFormulario} className="cerrar-button">
                <i className="bi bi-x-lg"></i>
              </button>
              <PedidoForm
                clientes={clientes}
                guardarOrden={guardarOrden}
                modoEdicion={modoEdicion}
                cotizacion={cotizacionParaOrden}
                orden={ordenSeleccionada}
                onFormDirtyChange={handleFormDirtyChange}
                closeModal={closeModalFormulario}
                setOrdenes={setOrdenes}
              />
            </Modal>
          </div>
          <div className="tabla-cotizaciones-footer">
            <div className="paginador-container">
              <Paginador
                totalItems={totalOrdenes}
                itemsPerPage={itemsPerPage}
                currentPage={currentPage}
                onPageChange={handlePageChange}
                onItemsPerPageChange={handleItemsPerPageChange}
              />
            </div>
          </div>
        </div>

        {/* Modal para vista previa / detalles del pedido */}
        <Modal
          isOpen={modalIsOpen}
          onRequestClose={cerrarModalPrevia}
          contentLabel="Vista Previa"
          style={customStyles}
        >
          {ordenSeleccionada && (
            <PreviaPedidos
              orden={ordenSeleccionada}
              numeroOrden={ordenSeleccionada.numeroOrdenDeCompra}
              clientes={clientes}
              cerrarPrevia={cerrarModalPrevia}
              onEditar={handleEditar}
            />
          )}
        </Modal>
      </div>

      <BalanceModal
        isOpen={showBalanceModal}
        onClose={() => setShowBalanceModal(false)}
        orderIds={balanceOrderIds}
      />
    </>
  );
}

export default TablaPedidos;
